<template>
  <v-card outlined class="jd-match-card mb-3">
    <v-card-title class="py-2 px-4">
      <div class="flex-grow-1">
        <div class="text-subtitle-1 font-weight-bold theme-heading">
          {{ result.name }}
        </div>
        <div class="text-caption grey--text text--darken-1">
          {{ result.currentTitle }} at {{ result.currentCompany }}
        </div>
      </div>
      <v-chip
        small
        :color="scoreColor"
        text-color="white"
        class="font-weight-bold"
      >
        {{ result.overallScore }}%
      </v-chip>
    </v-card-title>

    <v-divider />

    <v-card-text class="py-3 px-4">
      <div class="d-flex align-center mb-3">
        <v-icon small color="#F59E0B" class="mr-1">mdi-file-document-outline</v-icon>
        <span class="text-caption font-weight-medium theme-heading">
          Match against: {{ jobTitle }}
        </span>
      </div>

      <div v-if="result.categoryExplanation" class="text-caption mb-2" style="color: #555">
        {{ result.categoryExplanation }}
      </div>

      <div class="mb-3" v-if="overlappingSkills.length > 0">
        <div class="text-caption grey--text mb-1">Matching Skills</div>
        <v-chip
          v-for="skill in overlappingSkills"
          :key="skill"
          x-small
          color="#E8F5E9"
          text-color="#10B981"
          class="mr-1 mb-1"
        >
          <v-icon x-small left>mdi-check</v-icon>
          {{ skill }}
        </v-chip>
      </div>

      <div class="mb-3" v-if="missingSkills.length > 0">
        <div class="text-caption grey--text mb-1">Missing Skills</div>
        <v-chip
          v-for="skill in missingSkills"
          :key="skill"
          x-small
          color="#FFF3E0"
          text-color="#F59E0B"
          class="mr-1 mb-1"
        >
          <v-icon x-small left>mdi-minus</v-icon>
          {{ skill }}
        </v-chip>
      </div>

      <div v-if="displaySeniority || displayCategory" class="d-flex align-center mb-2">
        <v-chip x-small outlined color="primary" class="mr-2" v-if="displaySeniority">
          {{ displaySeniority }}
        </v-chip>
        <v-chip x-small outlined :color="categoryColor" v-if="displayCategory">
          {{ displayCategory }}
        </v-chip>
      </div>

      <div v-if="fitSignals.length > 0" class="mb-2">
        <div v-for="(signal, sIdx) in fitSignals" :key="sIdx" class="d-flex align-center mb-1">
          <v-icon x-small color="#10B981" class="mr-1">mdi-check-circle</v-icon>
          <span class="text-caption">{{ signal }}</span>
        </div>
      </div>

      <v-expansion-panels flat accordion v-if="hasEvidence">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0 py-1">
            <span class="text-caption font-weight-bold" style="color: #007AFF">
              Evidence Analysis
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="breakdown" class="mb-3">
              <v-row dense class="mb-2">
                <v-col cols="6" sm="3">
                  <div class="score-metric pa-2">
                    <div class="d-flex align-center justify-center">
                      <span class="text-caption grey--text">Coverage</span>
                      <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                        </template>
                        <span class="text-caption">{{ metricExplanation('coverage') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="text-body-2 font-weight-bold theme-heading">{{ Math.round(breakdown.coveragePct || 0) }}%</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="score-metric pa-2">
                    <div class="d-flex align-center justify-center">
                      <span class="text-caption grey--text">Strength</span>
                      <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                        </template>
                        <span class="text-caption">{{ metricExplanation('avgStrength') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="text-body-2 font-weight-bold theme-heading">{{ ((breakdown.avgStrength || 0) * 100).toFixed(0) }}%</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="score-metric pa-2">
                    <div class="d-flex align-center justify-center">
                      <span class="text-caption grey--text">Proven</span>
                      <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                        </template>
                        <span class="text-caption">{{ metricExplanation('provenBonus') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="text-body-2 font-weight-bold theme-heading">{{ provenBonusDisplay }}</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="score-metric pa-2">
                    <div class="d-flex align-center justify-center">
                      <span class="text-caption grey--text">Recency</span>
                      <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                        </template>
                        <span class="text-caption">{{ metricExplanation('recency') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="text-body-2 font-weight-bold theme-heading">{{ ((breakdown.recencyFactor || 1) * 100).toFixed(0) }}%</div>
                  </div>
                </v-col>
              </v-row>

              <div v-if="breakdown.explanation" class="text-caption mb-3" style="color: #333">
                {{ breakdown.explanation }}
              </div>
            </div>

            <div v-if="facetBreakdowns.length > 0" class="mb-3">
              <div class="text-caption grey--text mb-1">Facet Breakdown</div>
              <div v-for="(facet, fIdx) in facetBreakdowns" :key="'fb-' + fIdx" class="mb-2">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-caption" :style="{ color: facet.matched ? '#1E3A5F' : '#999' }">
                    {{ facet.facetName }}
                    <v-icon x-small :color="facet.matched ? '#10B981' : '#E0E0E0'" class="ml-1">
                      {{ facet.matched ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </span>
                  <span class="text-caption font-weight-bold" :style="{ color: facet.matched ? '#10B981' : '#999' }">
                    {{ Math.round((facet.strength || 0) * 100) }}%
                  </span>
                </div>
                <v-progress-linear
                  :value="(facet.strength || 0) * 100"
                  :color="facet.matched ? '#10B981' : '#E0E0E0'"
                  height="4"
                  rounded
                />
                <div v-if="hasStarEvidence(facet)" class="star-evidence mt-1 pa-2">
                  <div v-if="facet.situation" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Situation:</span>
                    <span class="ml-1">{{ truncate(facet.situation, 200) }}</span>
                  </div>
                  <div v-if="facet.task" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Task:</span>
                    <span class="ml-1">{{ truncate(facet.task, 200) }}</span>
                  </div>
                  <div v-if="facet.action" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Action:</span>
                    <span class="ml-1">{{ truncate(facet.action, 200) }}</span>
                  </div>
                  <div v-if="facet.result" class="text-caption">
                    <span class="font-weight-bold" style="color: #10B981">Result:</span>
                    <span class="ml-1">{{ truncate(facet.result, 200) }}</span>
                  </div>
                </div>
                <div v-else-if="facet.evidenceSpan" class="text-caption grey--text mt-1" style="font-style: italic">
                  "{{ truncate(facet.evidenceSpan, 120) }}"
                </div>
              </div>
            </div>

            <div v-else-if="hasFacetMatches">
              <div class="text-caption grey--text mb-1">JD Facet Breakdown</div>
              <div
                v-for="(fm, fmIdx) in normalizedFacetMatches"
                :key="'fm-' + fmIdx"
                class="mb-2"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-caption">{{ fm.name }}</span>
                  <span class="text-caption font-weight-bold" :style="{ color: getFacetColor(fm.score) }">
                    {{ fm.score }}%
                  </span>
                </div>
                <v-progress-linear
                  :value="fm.score"
                  :color="getFacetColor(fm.score)"
                  height="4"
                  rounded
                />
                <div v-if="fm.evidence" class="text-caption grey--text mt-1" style="font-style: italic">
                  "{{ truncate(fm.evidence, 120) }}"
                </div>
              </div>
            </div>

            <div v-if="result.strongestSignal" class="mt-2">
              <div class="text-caption grey--text">Strongest Signal</div>
              <div class="text-caption font-weight-bold" style="color: #10B981">{{ result.strongestSignal }}</div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-card-actions class="px-4 py-2">
      <v-spacer />
      <v-btn small text color="#007AFF" @click="$emit('view-profile', result)">
        <v-icon small left>mdi-account-eye</v-icon>
        View Profile
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "JDMatchCard",
  props: {
    result: {
      type: Object,
      required: true,
    },
    jobTitle: {
      type: String,
      default: "Job Description",
    },
  },
  computed: {
    scoreColor() {
      if (this.result.overallScore >= 70) return "#10B981";
      if (this.result.overallScore >= 50) return "#F59E0B";
      return "#9E9E9E";
    },
    displaySeniority() {
      var sen = (this.result.seniorityLevel || "").trim();
      var hidden = ["unknown", "none", "n/a", "undefined", "mid", "mid-level", ""];
      if (hidden.indexOf(sen.toLowerCase()) >= 0) return "";
      var labels = {
        "junior": "Junior",
        "mid": "",
        "mid-level": "",
        "senior": "Senior",
        "lead": "Lead",
        "principal": "Principal",
        "staff": "Staff",
        "director": "Director",
        "vp": "VP",
        "c-level": "C-Level",
        "executive": "Executive",
        "manager": "Manager",
      };
      return labels[sen.toLowerCase()] || sen;
    },
    displayCategory() {
      var cat = (this.result.matchCategory || "").trim();
      var hidden = ["unknown", "none", "n/a", "undefined", "potential match", ""];
      if (hidden.indexOf(cat.toLowerCase()) >= 0) return "";
      return cat;
    },
    categoryColor() {
      var colors = {
        "Best Match": "#10B981",
        "Strong Match": "#10B981",
        "Match": "#007AFF",
        "Good Match": "#007AFF",
        "Partial Match": "#F59E0B",
        "Potential Fit": "#F59E0B",
        "Weak Match": "#9E9E9E",
        "Consider": "#9E9E9E",
      };
      return colors[this.displayCategory] || "#007AFF";
    },
    overlappingSkills() {
      return this.result.overlappingSkills || this.result.keySkills?.slice(0, 5) || [];
    },
    missingSkills() {
      return this.result.missingSkills || [];
    },
    fitSignals() {
      return (this.result.fitSignals || []).slice(0, 3);
    },
    breakdown() {
      return this.result.scoreBreakdown || null;
    },
    facetBreakdowns() {
      if (!this.breakdown) return [];
      return this.breakdown.facetBreakdowns || [];
    },
    provenBonusDisplay() {
      var pb = this.breakdown ? this.breakdown.provenBonus || 1 : 1;
      var pct = ((pb - 1) * 100).toFixed(0);
      return pct > 0 ? "+" + pct + "%" : "0%";
    },
    normalizedFacetMatches() {
      var fm = this.result.facetMatches;
      if (!fm) return [];
      if (Array.isArray(fm)) {
        return fm.map(function(f) {
          return {
            name: f.facetName || f.name || '',
            score: f.relevanceScore || f.score || 0,
            evidence: f.evidenceSnippet || f.evidence || ''
          };
        });
      }
      return Object.keys(fm).map(function(key) {
        var val = fm[key];
        return {
          name: key,
          score: typeof val === 'number' ? val : 0,
          evidence: ''
        };
      });
    },
    hasFacetMatches() {
      return this.normalizedFacetMatches.length > 0;
    },
    hasEvidence() {
      return this.facetBreakdowns.length > 0 || this.hasFacetMatches || this.breakdown || this.result.strongestSignal;
    },
  },
  methods: {
    getFacetColor(value) {
      if (value >= 70) return "#10B981";
      if (value >= 50) return "#F59E0B";
      return "#EF4444";
    },
    truncate(text, maxLen) {
      if (!text) return "";
      return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
    },
    hasStarEvidence(facet) {
      if (!facet) return false;
      return !!(facet.situation || facet.task || facet.action || facet.result);
    },
    metricExplanation(metric) {
      var fallbacks = {
        coverage: "Share of requested facets that have at least some matching evidence in the profile, weighted by facet importance.",
        avgStrength: "Average evidence strength across matched facets. Strength is higher for STAR achievements with quantified outcomes.",
        provenBonus: "Bonus when claims are backed by STAR or small-win evidence; penalty when too many claims are skill-only without demonstration.",
        recency: "How recent the supporting evidence is. Drops as evidence ages beyond the last two years.",
      };
      var explanations = this.breakdown && this.breakdown.metricExplanations;
      if (explanations && explanations[metric]) return explanations[metric];
      return fallbacks[metric] || "";
    },
  },
};
</script>

<style scoped>
.star-evidence {
  background: rgba(99, 102, 241, 0.06);
  border-left: 3px solid #6366F1;
  border-radius: 4px;
}
.theme--dark .star-evidence {
  background: rgba(99, 102, 241, 0.12);
}
.jd-match-card {
  transition: box-shadow 0.2s ease;
}
.jd-match-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
.score-metric {
  background: #F5F7FA;
  border-radius: 6px;
  text-align: center;
}
</style>
